/**
 * Handing a generated file to the browser.
 *
 * The blob -> object URL -> hidden <a> -> click -> revoke dance is written out
 * by hand in nine places in this repo (consultation, observation and training
 * exports, the sociogram, the instruction service, the file-generation
 * service, and now the profile import). The copies have already drifted: some
 * put the anchor in the document before clicking and some do not, which is the
 * half that matters — Firefox ignores a click on an anchor that is not in the
 * DOM. New code should call this; the existing copies can migrate as they are
 * touched.
 */
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  // In the document, not just constructed: Firefox drops the click otherwise.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * The file name a server put in Content-Disposition, or null.
 *
 * Handles both forms Django emits: the RFC 5987 `filename*=UTF-8''…` that
 * carries Cyrillic intact, and the plain `filename="…"`. Returning null rather
 * than a placeholder lets the caller pick its own fallback, which is usually
 * more specific than anything this could invent.
 */
export function fileNameFromDisposition(disposition) {
  if (typeof disposition !== "string") return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // malformed percent-encoding — fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : null;
}

/**
 * A name folded into something safe to hand a filesystem.
 *
 * Callers pass school-authored text — a class name, a school name — which
 * arrives as anything: a space instead of a dash, a slash between parallel
 * groups. Windows refuses \ / : * ? " < > | outright and silently mangles a
 * name ending in a dot or a space, so the download either fails or lands under
 * a name nobody can find. Whitespace folds to an underscore, and the result is
 * capped so a pathological name cannot produce a path the OS truncates on its
 * own terms.
 *
 * Returns "" when nothing usable survives, so callers can fall back to a
 * generic name instead of emitting a file called ".xlsx".
 */
export const fileNameSafe = (value) =>
  String(value || "")
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^[_.]+|[_.]+$/g, "")
    .slice(0, 60);

/**
 * Excel caps a sheet name at 31 characters and throws on the excess, so a
 * translated title is trimmed rather than trusted — the Kazakh import title is
 * already 26. Kept here next to the other file-name rules because it is the
 * same class of constraint and was previously re-spelled at every call site.
 */
export const sheetName = (value) => String(value ?? "").slice(0, 31);
